import { ageGroup1, ageGroup4 } from '@/assets/messages';
import {
  AnnotatedType,
  PrimaryAnnotatedType,
  SecondaryAnnotatedType,
  TertiaryAnnotatedType,
} from '@/components/annotated-type';
import { VerticalBarChart } from '@/components/bar-chart/vertical';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { styled } from '@/styles/stitches.config';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const ChartRoot = styled('div', {
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  paddingInline: '$3',

  paddingBlockStart: '$9',
  paddingBlockEnd: '$8',

  '@portrait': {
    paddingBlockStart: '$9',
    paddingBlockEnd: '$9',
  },

  '@bp4': {
    paddingBlockStart: '$7',
    paddingBlockEnd: '$7',
  },
});

export function GlobalCitizens2023Chapter1({
  data,
}: {
  data: GlobalCitizensData['identify_with_world'];
}) {
  const { i18n } = useLingui();

  const answers = [
    {
      id: 'twoThirds',
      label: t({ id: 'global-citizens.chapter-2.answer-1', message: 'Only two-thirds as many' }),
    },
    {
      id: 'half',
      label: t({ id: 'global-citizens.chapter-2.answer-2', message: 'Only half as many' }),
    },
    {
      id: 'same',
      label: t({
        id: 'global-citizens.chapter-2.answer-3',
        message: 'About the same share as the young',
      }),
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion();

  const formatPercentage = useNumberFormatting('percent');

  return (
    <>
      <CenteredStoryText>
        <p>
          <Trans id="global-citizens-2023.chapter-1.text-1">
            A significant share of <TertiaryAnnotatedType>young people</TertiaryAnnotatedType> say
            they identify most with being part of the world.
          </Trans>
        </p>
        <p>
          <Trans id="global-citizens-2023.chapter-1.text-2">
            That's, in part, a reflection of the explosion of the internet – which helps connect
            people around the globe.
          </Trans>
        </p>
      </CenteredStoryText>
      <CenteredStoryText>
        <p>
          <Trans id="global-citizens-2023.chapter-1.text-3">
            We looked at whether <PrimaryAnnotatedType>young people</PrimaryAnnotatedType>—who have
            never known a world without internet—are{' '}
            <SecondaryAnnotatedType>more cosmopolitan</SecondaryAnnotatedType> than their elders.
          </Trans>
        </p>
      </CenteredStoryText>

      <StoryQuestion
        question={t({
          message:
            'Compared with the young, what share of older people identify as citizens of the world?',
          id: 'global-citizens-2023.chapter-1.question',
        })}
        answers={answers}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id &&
          t({
            id: 'global-citizens.chapter-2.question.response',
            message: select(selectedAnswer.id, {
              twoThirds: 'Not quite!',
              half: 'Correct!',
              same: 'Not quite!',
            }),
          })
        }
        onAnswer={handleAnswer}
      />

      <ScrollRoot>
        <ScrollGraphic>
          <ChartRoot>
            <VerticalBarChart
              css={{
                position: 'relative',
                flexGrow: '1',
              }}
              label={t({
                id: 'global-citizens-2023.chapter-1.chart-label',
                message: '% who say they identify most with being part of the world',
              })}
              formatValue={formatPercentage}
              data={[
                { label: ageGroup1(i18n), value: data['15_24'] },
                {
                  label: ageGroup4(i18n),
                  value: data['65plus'],
                },
              ]}
            />
          </ChartRoot>
        </ScrollGraphic>

        <ScrollSteps>
          <ScrollStep>
            <StoryText>
              <Trans id="global-citizens-2023.chapter-1.text-4">
                On average, just 14% of{' '}
                <SecondaryAnnotatedType>older people</SecondaryAnnotatedType> say they identify most
                with being part of the world — about half the share of{' '}
                <PrimaryAnnotatedType>young people</PrimaryAnnotatedType>.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep>
            <StoryText>
              <Trans id="global-citizens-2023.chapter-2.text-1">
                Looking across all 55 countries, the{' '}
                <PrimaryAnnotatedType>younger</PrimaryAnnotatedType> a person is, on average, the{' '}
                <TertiaryAnnotatedType>more likely</TertiaryAnnotatedType> s/he is to identify as a{' '}
                <TertiaryAnnotatedType>global citizen</TertiaryAnnotatedType>.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep>
            <StoryText>
              <Trans id="global-citizens-2023.chapter-2.text-2">
                This finding is <AnnotatedType>significant</AnnotatedType>: It reflects a{' '}
                <AnnotatedType>profound change</AnnotatedType> in how people think about themselves
                and with which community they most affiliate.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
